import React, { useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Line, Path, Text as SvgText } from 'react-native-svg';
import { FontFamily } from '../../../../../constants/fonts';
import type { WesHierarchyLevel, WesTimeGrain } from '../useWesPalletStagingFilters';
import { formatCount, type WesStagingScope, type WesTrend } from '../wesPalletStagingStaticRows';
import { hierarchyTitle } from './WesGeographyBars';
import WesSectionState, { type WesSectionStatus } from './WesSectionState';
import { WesTooltipTarget, estimateBubbleWidth } from './WesTooltip';
import { WES_CARD, WES_CARD_SUBTITLE, WES_CARD_TITLE, WES_CARD_TITLE_SUFFIX } from './wesCardStyle';

const GRAIN_TITLE: Record<WesTimeGrain, string> = { year: 'Year', period: 'Period', week: 'Week' };
const GEO_PLURAL: Record<WesHierarchyLevel, string> = { region: 'regions / units', market: 'markets / PODs', location: 'locations' };
const SCOPE_LOWER: Record<WesStagingScope, string> = { all: 'all', audit: 'audit-related', other: 'other' };

// Series palette from the mockup.
const COLORS = ['#034895', '#EB6B2D', '#1789EE', '#F5A623', '#2E9E6B', '#8E44AD', '#C12C01', '#16A2B8', '#6C757D', '#B8860B', '#E83E8C', '#20C997', '#5A4FCF', '#795548', '#FF7043', '#3F51B5', '#9CCC65'];
const HEIGHT = 320;
const PAD = { top: 12, right: 16, bottom: 52, left: 46 };
const POINT_SPACING = 44;
const HIT = 28;
const TENSION = 0.3;
// Tooltips above a point near the top of the plot would be clipped, so they open below it.
const ABOVE_MIN_Y = 70;
const LEGEND_MAX_HEIGHT = 96;

const AXIS_COLOR = '#777777';
const GRID_COLOR = '#EBEBEB';
const PLOT_BORDER_COLOR = '#D5DFED';

interface Pt {
  x: number;
  y: number;
}

// Chart.js style spline: control points weighted by neighbour distance, tension 0.3.
const smoothPath = (pts: Pt[]): string => {
  const control = (prev: Pt, cur: Pt, next: Pt) => {
    const d01 = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const d12 = Math.hypot(next.x - cur.x, next.y - cur.y);
    const sum = d01 + d12 || 1;
    const fa = (TENSION * d01) / sum;
    const fb = (TENSION * d12) / sum;
    return {
      before: { x: cur.x - fa * (next.x - prev.x), y: cur.y - fa * (next.y - prev.y) },
      after: { x: cur.x + fb * (next.x - prev.x), y: cur.y + fb * (next.y - prev.y) },
    };
  };
  let d = `M${pts[0].x} ${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = control(pts[i - 1] ?? pts[i], pts[i], pts[i + 1]);
    const b = control(pts[i], pts[i + 1], pts[i + 2] ?? pts[i + 1]);
    d += ` C${a.after.x} ${a.after.y} ${b.before.x} ${b.before.y} ${pts[i + 1].x} ${pts[i + 1].y}`;
  }
  return d;
};

// "2026 P5" becomes P5 over '26; a bare year stays on one line.
const labelLines = (label: string): string[] => {
  const [year, rest] = label.split(' ');
  return rest ? [rest, `'${year.slice(-2)}`] : [label];
};

interface WesTrendChartProps {
  trend: WesTrend;
  level: WesHierarchyLevel;
  grain: WesTimeGrain;
  scope?: WesStagingScope;
  // Series kept and series available, when the chart shows only the busiest ones.
  shown?: number;
  available?: number;
  status?: WesSectionStatus;
}

// Points with no data stay gaps: each run of consecutive values is its own path and a missing value gets no point or tooltip.
const WesTrendChart: React.FC<WesTrendChartProps> = ({ trend, level, grain, scope = 'all', shown, available, status }) => {
  const { buckets, series } = trend;
  const [selected, setSelected] = useState<string | null>(null);
  // Measured width of the scroll area; the plot stretches to it and scrolls only when the buckets need more room.
  const [viewWidth, setViewWidth] = useState(0);
  const spacing = Math.max(POINT_SPACING, buckets.length > 0 ? (viewWidth - PAD.left - PAD.right) / buckets.length : 0);
  const plotWidth = Math.max(buckets.length, 1) * spacing;
  const width = PAD.left + plotWidth + PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const x = (index: number) => PAD.left + (index + 0.5) * spacing;
  const y = (value: number) => PAD.top + (1 - Math.min(100, Math.max(0, value)) / 100) * plotHeight;
  const capped = shown !== undefined && available !== undefined && available > shown;
  const subtitle = `${SCOPE_LOWER[scope]} · one line per ${hierarchyTitle(level).toLowerCase()}${
    capped ? ` · Showing ${shown} of ${available} ${GEO_PLURAL[level]}. Filter to see others.` : ''
  }`;

  return (
    <View style={styles.card} testID="wes-trend">
      <Text style={styles.title} testID="wes-trend-title">
        {'% of Pallets Staged Over Time'}
        <Text style={styles.suffix}>{` by ${hierarchyTitle(level)} · by ${GRAIN_TITLE[grain]}`}</Text>
      </Text>
      <Text style={styles.subtitle} testID="wes-trend-subtitle">{subtitle}</Text>
      <WesSectionState status={status} skeleton="chart" isEmpty={series.length === 0} testID="wes-trend">
        <ScrollView horizontal showsHorizontalScrollIndicator onLayout={(e) => setViewWidth(e.nativeEvent.layout.width)} testID="wes-trend-scroll">
          <View style={{ width, height: HEIGHT }}>
            <Svg width={width} height={HEIGHT}>
              {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((tick) => (
                <React.Fragment key={tick}>
                  <Line x1={PAD.left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} stroke={GRID_COLOR} strokeWidth={1} />
                  <SvgText testID={`wes-trend-y-label-${tick}`} x={PAD.left - 8} y={y(tick) + 4} fontSize={12} fontFamily={FontFamily.regular} fill={AXIS_COLOR} textAnchor="end">{`${tick}%`}</SvgText>
                </React.Fragment>
              ))}
              <Line testID="wes-trend-plot-left" x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={y(0)} stroke={PLOT_BORDER_COLOR} strokeWidth={1} />
              <Line testID="wes-trend-plot-bottom" x1={PAD.left} x2={width - PAD.right} y1={y(0)} y2={y(0)} stroke={PLOT_BORDER_COLOR} strokeWidth={1} />
              {buckets.map((bucket, index) =>
                labelLines(bucket.label).map((line, li) => (
                  <SvgText key={`${bucket.key}-${li}`} testID={`wes-trend-x-label-${bucket.key}-${li}`} x={x(index)} y={HEIGHT - PAD.bottom + 20 + li * 16} fontSize={li === 0 ? 12 : 10} fontFamily={FontFamily.regular} fill={li === 0 ? AXIS_COLOR : '#9CA3AF'} textAnchor="middle">
                    {line}
                  </SvgText>
                )),
              )}
              {series.map((s, si) => {
                const color = COLORS[si % COLORS.length];
                const runs: { index: number; value: number }[][] = [];
                s.values.forEach((value, index) => {
                  if (value === null) {
                    runs.push([]);
                    return;
                  }
                  if (runs.length === 0) runs.push([]);
                  runs[runs.length - 1].push({ index, value });
                });
                return (
                  <React.Fragment key={s.label}>
                    {runs
                      .filter((run) => run.length > 1)
                      .map((run, ri) => (
                        <Path
                          key={`${s.label}-${ri}`}
                          testID={`wes-trend-path-${s.label}-${ri}`}
                          d={smoothPath(run.map((p) => ({ x: x(p.index), y: y(p.value) })))}
                          stroke={color}
                          strokeWidth={2}
                          fill="none"
                        />
                      ))}
                    {s.values.map((value, index) =>
                      value === null ? null : (
                        <Circle key={`${s.label}-${index}`} testID={`wes-trend-point-${s.label}-${index}`} cx={x(index)} cy={y(value)} r={buckets.length > 20 ? 2 : 3} fill={color} />
                      ),
                    )}
                  </React.Fragment>
                );
              })}
            </Svg>
            {series.map((s, seriesIndex) =>
              s.values.map((value, index) => {
                const counts = s.counts[index];
                if (value === null || !counts) return null;
                const key = `${s.label}-${index}`;
                const title = `${s.label} · ${buckets[index].label}`;
                const text = `${value.toFixed(1)}% (${formatCount(counts.staged)} of ${formatCount(counts.total)} pallets)`;
                const maxWidth = Math.min(360, width - 8);
                const bubbleWidth = Math.max(estimateBubbleWidth(title, maxWidth), estimateBubbleWidth(text, maxWidth - 18) + 18);
                const left = x(index) - HIT / 2;
                const bubbleLeft = Math.max(4 - left, Math.min(HIT / 2 - bubbleWidth / 2, width - bubbleWidth - 4 - left));
                return (
                  <WesTooltipTarget
                    key={key}
                    text={text}
                    title={title}
                    markerColor={COLORS[seriesIndex % COLORS.length]}
                    maxWidth={maxWidth}
                    testID={`wes-trend-hit-${key}`}
                    style={[styles.hit, { left, top: Math.max(0, y(value) - HIT / 2) }]}
                    placement={y(value) >= ABOVE_MIN_Y ? 'above' : 'below'}
                    bubbleLeft={bubbleLeft}
                    open={selected === key}
                    onOpenChange={(open) => setSelected((prev) => (open ? key : prev === key ? null : prev))}
                  >
                    <View />
                  </WesTooltipTarget>
                );
              }),
            )}
          </View>
        </ScrollView>
        <ScrollView style={styles.legendScroll} nestedScrollEnabled>
          <View style={styles.legend}>
            {series.map((s, si) => (
              <View key={s.label} style={styles.legendItem}>
                <View style={[styles.legendDot, { backgroundColor: COLORS[si % COLORS.length] }]} testID={`wes-legend-dot-${s.label}`} />
                <Text style={styles.legendText}>{s.label}</Text>
              </View>
            ))}
          </View>
        </ScrollView>
      </WesSectionState>
    </View>
  );
};

const styles = StyleSheet.create({
  card: { ...WES_CARD },
  title: WES_CARD_TITLE,
  suffix: WES_CARD_TITLE_SUFFIX,
  subtitle: WES_CARD_SUBTITLE,
  hit: { position: 'absolute', width: HIT, height: HIT, zIndex: 2 },
  legendScroll: { maxHeight: LEGEND_MAX_HEIGHT, marginTop: 10 },
  legend: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', columnGap: 18, rowGap: 6 },
  legendItem: { flexDirection: 'row', alignItems: 'center', columnGap: 6 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendText: { fontSize: 11, fontFamily: FontFamily.regular, color: '#666666' },
});

export default WesTrendChart;
